// Simple stroke feedback UI - shows toast notifications for actions

import type { Point, Stroke, StrokePoint } from "../types.ts";
import { getStrokeMetrics } from "../input/gestures.ts";

export type StrokeType = "closed" | "line" | "scribble";

export interface StrokeFeedback {
  type: StrokeType | "unknown";
  confidence: number;
  message: string;
  closeness: number;
}

/**
 * Analyze a stroke in progress and provide feedback
 */
export function analyzeStrokeInProgress(points: StrokePoint[]): StrokeFeedback {
  if (points.length < 5) {
    return { type: "unknown", confidence: 0, message: "", closeness: 0 };
  }

  const stroke: Stroke = { points, isClosed: false };
  const metrics = getStrokeMetrics(stroke);
  const closeness = calculateCloseness(points);

  // Check for scribble
  const isScribbly = metrics.significantTurns >= 4 ||
    (metrics.avgVelocity > 0.4 && metrics.significantTurns >= 2) ||
    (metrics.pathRatio > 3 && metrics.significantTurns >= 2);

  if (isScribbly) {
    return {
      type: "scribble",
      confidence: Math.min(0.9, 0.5 + metrics.significantTurns * 0.1),
      message: "delete",
      closeness,
    };
  }

  // Check for closed shape
  if (closeness > 0.6) {
    return {
      type: "closed",
      confidence: closeness > 0.85 ? 0.9 : 0.6,
      message: closeness > 0.85 ? "room" : "room?",
      closeness,
    };
  }

  return { type: "line", confidence: 0.7, message: "corridor", closeness };
}

/**
 * Calculate how "closed" a stroke is (0 = very open, 1 = perfectly closed)
 */
function calculateCloseness(points: StrokePoint[]): number {
  if (points.length < 3) return 0;

  const start = points[0];
  const end = points[points.length - 1];
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const endDistance = Math.sqrt(dx * dx + dy * dy);

  // Calculate bounding box
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const size = Math.max(maxX - minX, maxY - minY, 1);

  return Math.max(0, 1 - endDistance / (size * 0.5));
}

/**
 * Simple toast notification overlay
 */
export class StrokeFeedbackOverlay {
  private toast: HTMLElement;
  private hideTimeout: number | null = null;

  constructor(container: HTMLElement) {
    this.toast = document.createElement("div");
    this.toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      padding: 8px 16px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      border-radius: 4px;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      z-index: 1000;
      display: none;
      transition: opacity 0.2s;
    `;
    container.appendChild(this.toast);
  }

  hide(): void {
    this.toast.style.display = "none";
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  update(_feedback: StrokeFeedback, _position: Point): void {
    // Simplified: no longer show progress while drawing
  }

  showResult(
    message: string,
    type: "success" | "error" | "info",
    _position: Point
  ): void {
    const colors = {
      success: "#4a9",
      error: "#e55",
      info: "#58f",
    };

    this.toast.textContent = message;
    this.toast.style.background = colors[type];
    this.toast.style.display = "block";

    if (this.hideTimeout) clearTimeout(this.hideTimeout);
    this.hideTimeout = setTimeout(() => this.hide(), 2000) as unknown as number;
  }
}
